import React, { useEffect } from "react";
import { css } from "@emotion/react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";

import Manage from "core/Components/Management/Manage";
import { useAppState } from "core/contexts/AppContext";
import { useSSRStatus } from "core/contexts/SSRStatusContext";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import useMetadata, { MetaData } from "core/hooks/useMetadata";
import useRouter from "core/hooks/useRouter";
import { FLAG_SHOP_ONLINE_VIDEOS } from "core/includes/featureFlags";
import { hasFeatureFlag } from "core/includes/site";
import window from "core/includes/window";

import pages from "pages/config/pages";
import ErrorPage from "pages/Components/ErrorPage/ErrorPage";


type Props = {
    stage: string | null,
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
    urlSegmentName?: string,
};

/**
 * Handles the main rendering of pages.  Returns error pages if needed.
 * @constructor
 */
const Main: React.FC<Props> = ({ stage, showBreadcrumb, hideHeaderCountry = false, urlSegmentName }) => {
    const navigate = useNavigate();
    const [appState, setAppState] = useAppState();
    const user = appState?.user;
    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });

    const [data, loading, _error, internalPage] = useRouter(stage, urlSegmentName);
    let error = _error;
    const { setStatusCode } = useSSRStatus();
    const page = data?.page;
    const metaData = useMetadata(page);
    const hasShopOnlineVideoFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_ONLINE_VIDEOS);

    useEffect(() => {
        // loading is used for displaying <Footer>
        setAppState({ loading });
    }, [loading, setAppState]);

    if (page?.__typename === 'RedirectorPage') {
        if (page.linkUrl.indexOf('http') === 0) {
            window.location = page.linkUrl;
        } else {
            navigate(page.linkUrl);
        }
        return null;
    }

    let Component = page && Object.prototype.hasOwnProperty.call(pages, page.__typename) && pages[page.__typename];

    // Treat null page (not loading, no error) as 404
    const isMissingPage = !page && !loading && !error && !internalPage && data !== undefined;
    // Disallow access to OnlineEventPage without (1) correct feature flag or (2) urlSegmentName
    const isInvalidOnlineEventPage = page?.__typename === "OnlineEventPage" &&
        (!hasShopOnlineVideoFeatureFlag || !urlSegmentName);

    if (isMissingPage || isInvalidOnlineEventPage) {
        error = "404";
    }

    // Signal 404 status to SSR server (hard 404)
    if (error === "404") {
        setStatusCode(404);
    }

    const style = css`
        padding-top: ${headerHeight}px;
    `;

    return (
        <div css={style}>
            {!!user && internalPage === "profile" && hasShopOnlineVideoFeatureFlag && (
                <Manage />
            )}
            {(error || (page && !page.isClickable)) && (
                <ErrorPage error={error === "404" ? error : "500"} />
            )}
            {(!error && page && page.isClickable && Component) && (
                <>
                    <Helmet>
                        {metaData.map((meta: MetaData, index) => {
                            switch (meta.element) {
                                case 'link': return <link key={`meta-key-${index}`} {...meta.props} />;
                                case 'title': return <title key={`meta-key-${index}`} {...meta.props} />;
                                default: return <meta key={`meta-key-${index}`} {...meta.props} />;
                            }
                        })}
                    </Helmet>

                    <Component {...page && { data: page }} />
                </>
            )}
        </div>
    );
};

export default Main;
